import { apiFetch } from './client';

// ============================================================
// Charts API client - Fase 5.
// Mirror de GET /api/candles, GET /api/renko/:sessionId/blocks,
// GET /api/sessions/:sessionId/trades.
//
// Todos los times vienen como "YYYY-MM-DD HH:mm" Eastern wall-clock.
// El frontend los convierte a UTC seconds (epoch) para Lightweight Charts.
// ============================================================

export interface CandleDoc {
  time: string; // "YYYY-MM-DD HH:mm" Eastern
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface RenkoBlockDoc {
  index: number;
  time: string;
  startTime: string;
  direction: 'up' | 'down';
  open: number;
  close: number;
  boxSize: number;
  candleClose: number;
  candleHigh: number;
  candleLow: number;
  candleAtr: number;
  volume: number;
}

export interface SessionTradeDoc {
  orderId: string | null;
  side: 'long' | 'short' | null;
  status: 'open' | 'closed' | null;
  entryTime: string | null; // ISO UTC
  exitTime: string | null;
  // entryPrice / exitPrice: fill real de Tradovate (avgFillPrice).
  entryPrice: number | null;
  exitPrice: number | null;
  // entryTriggerPrice / exitTriggerPrice: precio teorico del engine.
  // null en ~22% de trades legacy pre-feature (PR-2 audit 2026-06-03).
  entryTriggerPrice: number | null;
  exitTriggerPrice: number | null;
  quantity: number | null;
  // pnl REAL (fill prices, incluye slippage) — leido directo de Mongo profitLoss.
  pnl: number;
  // btPnl: P&L sin slippage (trigger prices) — columna informativa del Trade List.
  btPnl: number;
  stopLoss: number | null;
  takeProfit: number | null;
  closeReason: string | null;
  valuePerPoint: number | null;
}

export type TradeStatusFilter = 'all' | 'open' | 'closed';

export async function fetchCandles(days: string[], symbol: string): Promise<CandleDoc[]> {
  if (days.length === 0 || !symbol) return [];
  const qs = new URLSearchParams({ days: days.join(','), symbol });
  const res = await apiFetch<{ candles: CandleDoc[] }>(`/api/candles?${qs}`);
  return res.candles;
}

export async function fetchRenkoBlocks(sessionId: string): Promise<RenkoBlockDoc[]> {
  const res = await apiFetch<{ blocks: RenkoBlockDoc[] }>(
    `/api/renko/${encodeURIComponent(sessionId)}/blocks`,
  );
  return res.blocks;
}

export async function fetchSessionTrades(
  sessionId: string,
  status: TradeStatusFilter = 'all',
): Promise<SessionTradeDoc[]> {
  const qs = status === 'all' ? '' : `?status=${status}`;
  const res = await apiFetch<{ trades: SessionTradeDoc[] }>(
    `/api/sessions/${encodeURIComponent(sessionId)}/trades${qs}`,
  );
  return res.trades;
}
